import { DragDrop, DragRef, CdkDragHandle } from '@angular/cdk/drag-drop';
import {
    afterNextRender,
    afterRenderEffect,
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    Injector,
    input,
    Input,
    signal,
    untracked,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { BehaviorSubject, of } from 'rxjs';
import { fromToolbar, ToolbarActions } from 'shared/classroom/toolbar/data-access';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { PCP_LOGGER } from '@pcp/logging';
import { observeResize, toSavedPosition } from './floating-toolbar-position';

const DRAWING_MENU_WIDTH = 280;
const DRAWING_MENU_OVERLAP = 20;

/**
 * Draggable drawing toolbar. The PARENT places it with CSS (no toolbar size, no timers): its default place
 * next to the open button, or the saved place (`{right, bottom}` from the frame's bottom-right corner).
 * This component only drags it, "bakes" a drag into the saved place, keeps it inside the frame and
 * picks the drawing-menu side.
 */
@Component({
    selector: 'pcp-slide-sync-floating-toolbar',
    templateUrl: './floating-toolbar.component.html',
    styleUrl: './floating-toolbar.component.scss',
    imports: [CdkDragHandle, NgClass, MatButton, MatIcon, MatIconButton, AsyncPipe],
})
export class FloatingToolbarComponent implements AfterViewInit {
    private readonly log = inject(PCP_LOGGER).createScoped('slide-sync');
    /** The frame: the drag boundary, and the box the saved place is measured against. */
    @Input({required: true}) boundary: HTMLElement;
    @ViewChild("dragHandle") dragHandle: ElementRef<HTMLElement>;
    /** Changes when the parent moves the toolbar (nav side in the default place, or the saved place). */
    readonly layoutKey = input<unknown>();

    private readonly isOpened = toSignal(this.store.select(fromToolbar.selectIsFloatingToolbarOpened), { initialValue: false });
    /** Bumped when the frame changes size (window, mobile address bar, scrollbar). */
    private readonly frameChange = signal(0);
    /** true between a drag's end and `dragRef.reset()`: the layer has moved, the CDK transform is still on. */
    private readonly baking = signal(false);
    private readonly injector = inject(Injector);
    private dragRef?: DragRef;

    readonly isVideoSlide$ = of(false);
    //uncomment to show the play/pause buttton:
    // readonly isVideoSlide$ = this.store.select(fromActiveElement.selectCurrentLayerSlide).pipe(
    //     map(slide => isNotNullOrUndefined(slide?.vimeoVideoId))
    // );

    private readonly dummyIsVideoStartedSubject = new BehaviorSubject(false);
    readonly isVideoStarted$ = this.dummyIsVideoStartedSubject.asObservable();
    //TODO: handle play and pause video button state:
    // this.store.select(fromActiveElement.primarySlideVideoStartedPlaying).pipe(
    //     map(slide => isNotNullOrUndefined(slide?.vimeoVideoId)),
    // );

    private destroyRef = inject(DestroyRef);

    constructor(
        private element: ElementRef<HTMLElement>,
        private dragDropService: DragDrop,
        private readonly store: Store,
    ) {
        // After each render that moved / showed the toolbar or changed the frame: position checks
        // (no toolbar size, no timer).
        afterRenderEffect({
            earlyRead: () => {
                this.layoutKey();
                this.frameChange();
                // While a drag is being baked, the layer has already moved but the CDK transform is still on:
                // the rect would count the drag twice. Measure again once `reset()` has dropped the transform.
                return this.isOpened() && this.boundary && !this.baking()
                    ? { rect: this.element.nativeElement.getBoundingClientRect(), frame: this.boundary.getBoundingClientRect() }
                    : null;
            },
            write: measured => {
                const m = measured();
                if (m) {
                    // untracked: DragRef.getFreeDragPosition() reads CDK signals (isDragging()). Tracked, they would
                    // re-run this phase on the next pointer down, with this (by then stale) measurement (#33829).
                    untracked(() => {
                        this.keepInsideFrame(m.rect, m.frame);
                        this.dispatchSubmenuPosition(m.rect.left - m.frame.left);
                    });
                }
            },
        });
    }

    ngAfterViewInit(): void {
        this.initializeDragNDrop();
    }

    videoStart() {
        this.dummyIsVideoStartedSubject.next(true);
    }

    videoPause() {
        this.dummyIsVideoStartedSubject.next(false);
    }

    private initializeDragNDrop() {
        const dragRef = this.dragDropService.createDrag(this.element).withHandles([this.dragHandle]);
        this.dragRef = dragRef;
        // createDrag() is not tied to this component (unlike the cdkDrag directive): dispose it ourselves,
        // or its registry entry, handle listeners and viewport-resize subscription outlive the toolbar.
        this.destroyRef.onDestroy(() => {
            dragRef.dispose();
            this.dragRef = undefined;
        });

        if (this.boundary) {
            dragRef.withBoundaryElement(this.boundary);
            observeResize(this.boundary).pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.frameChange.update(n => n + 1));
        } else {
            this.log.error("drag n drop will not work for FloatingToolbarComponent without 'boundary' @Input property");
        }

        // "Bake" the drag into the saved place. From the default place the parent then renders a NEW toolbar
        // in the saved layer (this one is destroyed, so the reset below never runs). From the saved place the
        // layer moves, and the CDK transform is dropped once that is rendered: at rest there is no transform.
        // `baking` pauses the position checks in between (see the `earlyRead` phase).
        dragRef.ended.asObservable().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.baking.set(true);
            this.store.dispatch(ToolbarActions.setToolbarPosition({
                position: toSavedPosition(this.element.nativeElement.getBoundingClientRect(), this.boundary.getBoundingClientRect()),
            }));
            afterNextRender(() => {
                dragRef.reset();
                this.baking.set(false);
            }, { injector: this.injector });
        });
    }

    /** If the toolbar sticks out of the frame (small window, frame shrank), move it in (not saved). Position only. */
    private keepInsideFrame(rect: DOMRect, frame: DOMRect) {
        const dx = Math.max(0, frame.left - rect.left) - Math.max(0, rect.right - frame.right);
        const dy = Math.max(0, frame.top - rect.top) - Math.max(0, rect.bottom - frame.bottom);
        if (this.dragRef && (dx !== 0 || dy !== 0)) {
            const { x, y } = this.dragRef.getFreeDragPosition();
            this.dragRef.setFreeDragPosition({ x: x + dx, y: y + dy });
            this.log.debug('keepInsideFrame', { dx, dy });
        }
    }

    /** The drawing menu opens to the left when there is room on the left (same rule as before). */
    private dispatchSubmenuPosition(leftInFrame: number) {
        const threshold = DRAWING_MENU_WIDTH - DRAWING_MENU_OVERLAP;
        this.store.dispatch(ToolbarActions.setSubmenuToolbarPosition({
            position: leftInFrame > threshold ? 'left' : 'right',
        }));
    }

    onHideFloatingToolbar = () =>
        this.store.dispatch(ToolbarActions.closeFloatingToolbar());
}
